import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUndoToast } from '@/components/Ui/Toast';
import { UiLink } from '@/components/Ui/UiLink';
import { requiredFileField, testSubmissionData } from '@/lib/form-site';
import { sendTestSubmission } from '@/lib/test-submission';
import { useRustyState } from '@/state';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

interface Props extends Pick<SectionProps, 'form' | 'spec' | 'data'> {
  /** Where the test's email field points; the first verified recipient when there is one. */
  email: string | null;
}

const KNOWN_STATUS = [400, 402, 403, 429];

/** Posts a real, billed submission from this browser, solving the captcha like the widget would. */
export function TestSubmission({ form, spec, data, email }: Props) {
  const { t } = useTranslation();
  const notify = useUndoToast();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const origin = window.location.origin;
  const origins = form.allowed_origins ?? [];
  const blockedByOrigins = origins.length > 0 && !origins.includes(origin);
  const fileField = requiredFileField(spec);

  const send = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const result = await sendTestSubmission(
        form.id,
        testSubmissionData(spec, email ?? 'test@example.com'),
        form.check_challenge
      );
      if (result.ok) {
        notify(t('form.test.sent'));
        await data.reloadStatus();
        const state = useRustyState.getState();
        void state.refreshUnreadCount();
        void state.refreshProblemsCount();
      } else {
        const reason = KNOWN_STATUS.includes(result.status)
          ? t(`form.test.status.${result.status}`)
          : t('form.test.status.other', { status: result.status });
        setFailure(result.detail ? `${reason} ${result.detail}` : reason);
      }
    } catch (e) {
      setFailure(t('form.test.failed', { detail: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={classes.stack}>
      <div className={classes.testLine}>
        <Button
          size="xs"
          onClick={() => void send()}
          loading={busy}
          disabled={blockedByOrigins || !!fileField}
        >
          {t('form.test.button')}
        </Button>
        <span className={classes.help}>{t('form.test.help')}</span>
      </div>
      {blockedByOrigins && (
        <span className={classes.help}>
          {t('form.test.blockedByOrigins', { origin })}{' '}
          <UiLink to="#origins">{t('form.after.origins')}</UiLink>
        </span>
      )}
      {fileField && (
        <span className={classes.help}>{t('form.test.fileRequired', { field: fileField })}</span>
      )}
      {failure && (
        <span className={classes.risk} role="status">
          {failure}
        </span>
      )}
    </div>
  );
}
